import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Upload, 
  Camera, 
  FileImage, 
  User, 
  Building, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Check,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Edit2,
  Save,
  UserPlus,
  FileText
} from 'lucide-react';
import { useTenantProtection } from '../../hooks/useTenantProtection';

const BusinessCardScanner = () => {
  const { t } = useTranslation();
  const { getProtectedTenantCode } = useTenantProtection();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [customerCreated, setCustomerCreated] = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isProcessingOpenAI, setIsProcessingOpenAI] = useState(false);
  const [openAIResult, setOpenAIResult] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [isProcessingLLaVA, setIsProcessingLLaVA] = useState(false);
  const [llavaResult, setLLaVAResult] = useState(null);
  const [isProcessingOpenRouter, setIsProcessingOpenRouter] = useState(false);
  const [openRouterResult, setOpenRouterResult] = useState(null);
  const [isProcessingGemini, setIsProcessingGemini] = useState(false);
  const [geminiResult, setGeminiResult] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [extractionMethod, setExtractionMethod] = useState(null);

  // Helper function to get headers with tenant code
  const getApiHeaders = (contentType = null) => {
    const tenantCode = getProtectedTenantCode();
    const headers = {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
    };
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    return headers;
  };

  // Helper function to calculate business card confidence
  const calculateBusinessCardConfidence = (businessCardData, enrichedCompany) => {
    if (!businessCardData) return 0;
    
    let score = 0;
    
    // Basic information scoring
    if (businessCardData.person?.firstName) score += 0.2;
    if (businessCardData.person?.lastName) score += 0.2;
    if (businessCardData.person?.email) score += 0.15;
    if (businessCardData.person?.phone) score += 0.15;
    if (businessCardData.company?.name) score += 0.2;
    if (enrichedCompany && Object.keys(enrichedCompany).length > 0) score += 0.1;
    
    // Additional scoring for job title and Thai text support
    if (businessCardData.person?.title) score += 0.1;
    if (businessCardData.company?.website) score += 0.05;
    if (businessCardData.company?.address) score += 0.05;
    
    return Math.min(1.0, score);
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setExtractedData(null);
        setEditedData(null);
        setDetectionResult(null);
        setOpenAIResult(null);
        setLLaVAResult(null);
        setOpenRouterResult(null);
        setGeminiResult(null);
        setShowComparison(false);
        setError(null);
        setCustomerCreated(false);
        setIsEditing(false);
        setExtractionMethod(null);
        setConfidence(0);
      } else {
        setError('Please select a valid image file (JPG, PNG, GIF)');
      }
    }
  };

  // Handle drag and drop
  const handleDrop = async (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setExtractedData(null);
        setEditedData(null);
        setDetectionResult(null);
        setOpenAIResult(null);
        setLLaVAResult(null);
        setOpenRouterResult(null);
        setGeminiResult(null);
        setShowComparison(false);
        setError(null);
        setCustomerCreated(false);
        setIsEditing(false);
        setExtractionMethod(null);
        setConfidence(0);
      } else {
        setError('Please select a valid image file (JPG, PNG, GIF)');
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Detect if image is a business card using lightweight OCR with a specific file
  const detectBusinessCardFromFile = async (file) => {
    if (!file) return;

    setIsDetecting(true);
    setError(null);
    setDetectionResult(null);

    try {
      const formData = new FormData();
      formData.append('businessCard', file);

      // Use lightweight OCR endpoint for quick detection
      const response = await fetch('/api/ai/vision/quick-detect-business-card', {
        method: 'POST',
        headers: getApiHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to detect business card');
      }

      const result = await response.json();
      setDetectionResult(result);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDetecting(false);
    }
  };

  // Detect if image is a business card using lightweight OCR
  const detectBusinessCard = async () => {
    if (!selectedFile) return;
    await detectBusinessCardFromFile(selectedFile);
  };

  // Process business card from a specific file
  const processBusinessCardFromFile = async (file) => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('businessCard', file);

      const response = await fetch('/api/ai/vision/extract-business-card', {
        method: 'POST',
        headers: getApiHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process business card');
      }

      const result = await response.json();
      setExtractedData(result.extractedData);
      setConfidence(result.confidence);
      setExtractionMethod(result.extractionMethod || result.configuredService);
      
      // Initialize editedData with proper bilingual structure
      const initData = {
        person: {
          firstName: result.extractedData?.person?.firstName || '',
          lastName: result.extractedData?.person?.lastName || '',
          firstName_eng: result.extractedData?.person?.firstName_eng || '',
          lastName_eng: result.extractedData?.person?.lastName_eng || '',
          title: result.extractedData?.person?.title || '',
          title_eng: result.extractedData?.person?.title_eng || '',
          email: result.extractedData?.person?.email || '',
          phone: result.extractedData?.person?.phone || ''
        },
        company: {
          name: result.extractedData?.company?.name || '',
          name_eng: result.extractedData?.company?.name_eng || '',
          address: result.extractedData?.company?.address || '',
          address_eng: result.extractedData?.company?.address_eng || '',
          website: result.extractedData?.company?.website || ''
        }
      };
      setEditedData(initData);
      setIsEditing(true);
      
    } catch (err) {
      setError(err.message);
      // Initialize empty editedData even on error so user can fill in manually
      const emptyData = {
        person: {
          firstName: '',
          lastName: '',
          firstName_eng: '',
          lastName_eng: '',
          title: '',
          title_eng: '',
          email: '',
          phone: ''
        },
        company: {
          name: '',
          name_eng: '',
          address: '',
          address_eng: '',
          website: ''
        }
      };
      setEditedData(emptyData);
      setIsEditing(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Process business card using configured model
  const processBusinessCard = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('businessCard', selectedFile);

      const response = await fetch('/api/ai/vision/extract-business-card', {
        method: 'POST',
        headers: getApiHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process business card');
      }

      const result = await response.json();
      setExtractedData(result.extractedData);
      setConfidence(result.confidence);
      setExtractionMethod(result.extractionMethod || result.configuredService);
      
      // Initialize editedData with proper bilingual structure
      const initData = {
        person: {
          firstName: result.extractedData?.person?.firstName || '',
          lastName: result.extractedData?.person?.lastName || '',
          firstName_eng: result.extractedData?.person?.firstName_eng || '',
          lastName_eng: result.extractedData?.person?.lastName_eng || '',
          title: result.extractedData?.person?.title || '',
          title_eng: result.extractedData?.person?.title_eng || '',
          email: result.extractedData?.person?.email || '',
          phone: result.extractedData?.person?.phone || ''
        },
        company: {
          name: result.extractedData?.company?.name || '',
          name_eng: result.extractedData?.company?.name_eng || '',
          address: result.extractedData?.company?.address || '',
          address_eng: result.extractedData?.company?.address_eng || '',
          website: result.extractedData?.company?.website || ''
        }
      };
      setEditedData(initData);
      setIsEditing(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Process business card with OCR (legacy)
  const processBusinessCardWithOCR = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('businessCard', selectedFile);

      const response = await fetch('/api/ai/vision/ingest-business-card', {
        method: 'POST',
        headers: getApiHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process business card');
      }

      const result = await response.json();
      setExtractedData(result.extractedData);
      setConfidence(result.confidence);
      setExtractionMethod('ocr');
      
      // Initialize editedData with proper bilingual structure
      const initData = {
        person: {
          firstName: result.extractedData?.person?.firstName || '',
          lastName: result.extractedData?.person?.lastName || '',
          firstName_eng: result.extractedData?.person?.firstName_eng || '',
          lastName_eng: result.extractedData?.person?.lastName_eng || '',
          title: result.extractedData?.person?.title || '',
          title_eng: result.extractedData?.person?.title_eng || '',
          email: result.extractedData?.person?.email || '',
          phone: result.extractedData?.person?.phone || ''
        },
        company: {
          name: result.extractedData?.company?.name || '',
          name_eng: result.extractedData?.company?.name_eng || '',
          address: result.extractedData?.company?.address || '',
          address_eng: result.extractedData?.company?.address_eng || '',
          website: result.extractedData?.company?.website || ''
        }
      };
      setEditedData(initData);
      setIsEditing(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Process business card with OpenAI
  const processBusinessCardWithOpenAI = async () => {
    if (!selectedFile) return;

    setIsProcessingOpenAI(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('businessCard', selectedFile);

      const response = await fetch('/api/ai/vision/extract-business-card-openai', {
        method: 'POST',
        headers: getApiHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process business card with OpenAI');
      }

      const result = await response.json();
      setOpenAIResult(result);
      setExtractedData(result.openaiResult);
      setConfidence(result.openaiResult.extractionDetails.confidence);
      
      // Initialize editedData with proper bilingual structure for OpenAI result
      const initData = {
        person: {
          firstName: result.openaiResult?.person?.firstName || '',
          lastName: result.openaiResult?.person?.lastName || '',
          firstName_eng: result.openaiResult?.person?.firstName_eng || '',
          lastName_eng: result.openaiResult?.person?.lastName_eng || '',
          title: result.openaiResult?.person?.title || '',
          title_eng: result.openaiResult?.person?.title_eng || '',
          email: result.openaiResult?.person?.email || '',
          phone: result.openaiResult?.person?.phone || ''
        },
        company: {
          name: result.openaiResult?.company?.name || '',
          name_eng: result.openaiResult?.company?.name_eng || '',
          address: result.openaiResult?.company?.address || '',
          address_eng: result.openaiResult?.company?.address_eng || '',
          website: result.openaiResult?.company?.website || ''
        }
      };
      setEditedData(initData);
      setIsEditing(true);
      setShowComparison(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessingOpenAI(false);
    }
  };

  // Process business card with LLaVA
  const processBusinessCardWithLLaVA = async () => {
    if (!selectedFile) return;

    setIsProcessingLLaVA(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('businessCard', selectedFile);

      const response = await fetch('/api/ai/vision/extract-business-card-llava', {
        method: 'POST',
        headers: getApiHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process business card with LLaVA');
      }

      const result = await response.json();
      setLLaVAResult(result);
      setExtractedData(result.llavaResult);
      setConfidence(result.llavaResult.extractionDetails.confidence);
      
      // Initialize editedData with proper bilingual structure for LLaVA result
      const initData = {
        person: {
          firstName: result.llavaResult?.person?.firstName || '',
          lastName: result.llavaResult?.person?.lastName || '',
          firstName_eng: result.llavaResult?.person?.firstName_eng || '',
          lastName_eng: result.llavaResult?.person?.lastName_eng || '',
          title: result.llavaResult?.person?.title || '',
          title_eng: result.llavaResult?.person?.title_eng || '',
          email: result.llavaResult?.person?.email || '',
          phone: result.llavaResult?.person?.phone || ''
        },
        company: {
          name: result.llavaResult?.company?.name || '',
          name_eng: result.llavaResult?.company?.name_eng || '',
          address: result.llavaResult?.company?.address || '',
          address_eng: result.llavaResult?.company?.address_eng || '',
          website: result.llavaResult?.company?.website || ''
        }
      };
      setEditedData(initData);
      setIsEditing(true);
      setShowComparison(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessingLLaVA(false);
    }
  };

  // Process business card with OpenRouter
  const processBusinessCardWithOpenRouter = async () => {
    if (!selectedFile) return;

    setIsProcessingOpenRouter(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('businessCard', selectedFile);

      const response = await fetch('/api/ai/vision/extract-business-card-openrouter', {
        method: 'POST',
        headers: getApiHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process business card with OpenRouter');
      }

      const result = await response.json();
      setOpenRouterResult(result);
      setExtractedData(result.openrouterResult);
      setConfidence(result.openrouterResult.extractionDetails.confidence);
      
      // Initialize editedData with proper bilingual structure for OpenRouter result
      const initData = {
        person: {
          firstName: result.openrouterResult?.person?.firstName || '',
          lastName: result.openrouterResult?.person?.lastName || '',
          firstName_eng: result.openrouterResult?.person?.firstName_eng || '',
          lastName_eng: result.openrouterResult?.person?.lastName_eng || '',
          title: result.openrouterResult?.person?.title || '',
          title_eng: result.openrouterResult?.person?.title_eng || '',
          email: result.openrouterResult?.person?.email || '',
          phone: result.openrouterResult?.person?.phone || ''
        },
        company: {
          name: result.openrouterResult?.company?.name || '',
          name_eng: result.openrouterResult?.company?.name_eng || '',
          address: result.openrouterResult?.company?.address || '',
          address_eng: result.openrouterResult?.company?.address_eng || '',
          website: result.openrouterResult?.company?.website || ''
        }
      };
      setEditedData(initData);
      setIsEditing(true);
      setShowComparison(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessingOpenRouter(false);
    }
  };

  // Process business card with Gemini
  const processBusinessCardWithGemini = async () => {
    if (!selectedFile) return;

    setIsProcessingGemini(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('businessCard', selectedFile);

      const response = await fetch('/api/ai/vision/extract-business-card-gemini', {
        method: 'POST',
        headers: getApiHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process business card with Gemini');
      }

      const result = await response.json();
      setGeminiResult(result);
      setExtractedData(result.geminiResult);
      setConfidence(result.geminiResult.extractionDetails.confidence);
      
      // Initialize editedData with proper bilingual structure for Gemini result
      const initData = {
        person: {
          firstName: result.geminiResult?.person?.firstName || '',
          lastName: result.geminiResult?.person?.lastName || '',
          firstName_eng: result.geminiResult?.person?.firstName_eng || '',
          lastName_eng: result.geminiResult?.person?.lastName_eng || '',
          title: result.geminiResult?.person?.title || '',
          title_eng: result.geminiResult?.person?.title_eng || '',
          email: result.geminiResult?.person?.email || '',
          phone: result.geminiResult?.person?.phone || ''
        },
        company: {
          name: result.geminiResult?.company?.name || '',
          name_eng: result.geminiResult?.company?.name_eng || '',
          address: result.geminiResult?.company?.address || '',
          address_eng: result.geminiResult?.company?.address_eng || '',
          website: result.geminiResult?.company?.website || ''
        }
      };
      setEditedData(initData);
      setIsEditing(true);
      setShowComparison(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessingGemini(false);
    }
  };

  // Create customer from extracted data
  const createCustomer = async () => {
    if (!editedData) return;

    setIsCreatingCustomer(true);
    setError(null);

    try {
      // Helper function to convert empty strings to null
      const sanitizeValue = (value) => {
        if (typeof value === 'string' && value.trim() === '') {
          return null;
        }
        return value || null;
      };

      // Create bilingual customer data with both person and company
      const customerData = {
        customerType: 'Person',
        person: {
          // Thai names
          firstName: sanitizeValue(editedData.person.firstName),
          lastName: sanitizeValue(editedData.person.lastName),
          // English names  
          firstName_eng: sanitizeValue(editedData.person.firstName_eng),
          lastName_eng: sanitizeValue(editedData.person.lastName_eng),
          title: sanitizeValue(editedData.person.title),
          email: sanitizeValue(editedData.person.email),
          phone: sanitizeValue(editedData.person.phone)
        },
        company: {
          // Thai company name
          companyName: sanitizeValue(editedData.company.name),
          // English company name
          companyName_eng: sanitizeValue(editedData.company.name_eng),
          address: sanitizeValue(editedData.company.address || editedData.company.address_eng),
          website: sanitizeValue(editedData.company.website)
        }
      };

      // Ensure we have at least Thai name or English name
      if (!customerData.person.firstName && !customerData.person.firstName_eng) {
        throw new Error('At least one name (Thai or English) is required');
      }

      // Validate email format if provided
      if (customerData.person.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.person.email)) {
        throw new Error('Please provide a valid email address');
      }

      // Always create company if we have company information
      if (!customerData.company.companyName && !customerData.company.companyName_eng) {
        customerData.company = null;
      }

      // Log the data being sent for debugging
      console.log('Creating customer with data:', JSON.stringify(customerData, null, 2));

      // Step 1: Create the customer
      const customerResponse = await fetch('/api/customers', {
        method: 'POST',
        headers: getApiHeaders('application/json'),
        body: JSON.stringify(customerData)
      });

      if (!customerResponse.ok) {
        let errorMessage = 'Failed to create customer';
        try {
          const errorData = await customerResponse.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('Customer creation error response:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          const textError = await customerResponse.text();
          console.error('Raw error response:', textError);
          errorMessage = `HTTP ${customerResponse.status}: ${customerResponse.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const customerResult = await customerResponse.json();
      const customerId = customerResult.customerId || customerResult.CustomerID;

      // Step 2: Upload business card as attachment if we have a file
      if (selectedFile && customerId) {
        try {
          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('title', 'Business Card');
          formData.append('description', `Business card for ${customerData.person.firstName || customerData.person.firstName_eng} ${customerData.person.lastName || customerData.person.lastName_eng}`);
          formData.append('tags', 'business-card,scanned,contact');

          const attachmentResponse = await fetch(`/api/attachments/Customer/${customerId}/upload`, {
            method: 'POST',
            headers: getApiHeaders(),
            body: formData
          });

          if (attachmentResponse.ok) {
            const attachmentResult = await attachmentResponse.json();
            console.log('Business card attached successfully:', attachmentResult);
          } else {
            console.warn('Failed to attach business card, but customer was created successfully');
          }
        } catch (attachmentError) {
          console.warn('Error attaching business card:', attachmentError.message);
          // Don't fail the entire operation if attachment fails
        }
      }

      setCustomerCreated(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  // Update edited data
  const updateEditedData = (field, value) => {
    const fieldParts = field.split('.');
    
    if (fieldParts.length === 2) {
      // Handle nested fields like 'person.firstName' or 'company.name'
      const [category, key] = fieldParts;
      setEditedData(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [key]: value
        }
      }));
    } else {
      // Handle top-level fields
      setEditedData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Check if image looks like a business card
  const isBusinessCard = () => {
    if (!extractedData) return false;
    
    const hasPersonalInfo = extractedData.person.firstName || extractedData.person.lastName;
    const hasContactInfo = extractedData.person.email || extractedData.person.phone;
    const hasCompanyInfo = extractedData.company.name;
    
    return hasPersonalInfo && (hasContactInfo || hasCompanyInfo);
  };

  // Reset all states
  const resetScanner = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setExtractedData(null);
    setEditedData(null);
    setDetectionResult(null);
    setOpenAIResult(null);
    setLLaVAResult(null);
    setOpenRouterResult(null);
    setGeminiResult(null);
    setConfidence(0);
    setError(null);
    setIsEditing(false);
    setCustomerCreated(false);
    setShowComparison(false);
    setShowAdvancedOptions(false);
    setExtractionMethod(null);
    setIsProcessing(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Business Card Scanner</h1>
          <p className="text-gray-600 dark:text-gray-300">Upload a business card image to extract contact information</p>
        </div>
        {(selectedFile || extractedData) && (
          <button
            onClick={resetScanner}
            className="bg-gray-600 dark:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-700 dark:hover:bg-gray-600"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Reset</span>
          </button>
        )}
      </div>

      {/* Upload Area */}
      {!selectedFile && (
        <div
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div className="space-y-4">
            <div className="flex justify-center">
              <FileImage className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900 dark:text-white">Upload Business Card Image</p>
              <p className="text-gray-600 dark:text-gray-300">Drag and drop or click to select</p>
            </div>
            <div className="flex justify-center">
              <label className="bg-blue-600 dark:bg-blue-700 text-white px-6 py-3 rounded-lg cursor-pointer hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2">
                <Upload className="h-5 w-5" />
                <span>Select Image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Supported formats: JPG, PNG, GIF</p>
          </div>
        </div>
      )}

      {/* Preview and Process */}
      {selectedFile && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Image Preview */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Camera className="h-5 w-5 mr-2" />
              Uploaded Image
            </h3>
            <div className="space-y-4">
              <img
                src={previewUrl}
                alt="Business card preview"
                className="w-full h-auto max-h-96 object-contain border border-gray-200 dark:border-gray-600 rounded-lg"
              />
              
              {/* Extraction Buttons - Show when no data extracted yet */}
              {!editedData && !isProcessing && !isProcessingOpenAI && !isProcessingGemini && !isProcessingOpenRouter && !isProcessingLLaVA && (
                <div className="space-y-3">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-center space-x-2 text-blue-700 dark:text-blue-300 mb-2">
                      <FileImage className="h-4 w-4" />
                      <span className="text-sm font-medium">Ready to extract information</span>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      ✨ Will use your tenant's configured AI model for best accuracy
                    </p>
                  </div>

                  {/* Primary Extract Button */}
                  <button
                    onClick={() => processBusinessCardFromFile(selectedFile)}
                    className="w-full bg-blue-600 dark:bg-blue-700 text-white px-4 py-3 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center justify-center space-x-2 font-medium"
                  >
                    <FileImage className="h-5 w-5" />
                    <span>Extract Information</span>
                  </button>

                  {/* Advanced Options Toggle */}
                  <button
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                    className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 py-2 flex items-center justify-center space-x-1"
                  >
                    <span>{showAdvancedOptions ? '▼' : '▶'}</span>
                    <span>Advanced: Try Different AI Models</span>
                  </button>

                  {/* Advanced Model Options */}
                  {showAdvancedOptions && (
                    <div className="grid grid-cols-1 gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Try different AI models configured by your tenant:
                      </p>
                      <button
                        onClick={processBusinessCardWithOCR}
                        className="w-full bg-green-600 dark:bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center justify-center space-x-2 text-sm"
                      >
                        <FileImage className="h-4 w-4" />
                        <span>Tesseract OCR</span>
                      </button>
                      <button
                        onClick={processBusinessCardWithOpenAI}
                        className="w-full bg-purple-600 dark:bg-purple-700 text-white px-4 py-2 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 flex items-center justify-center space-x-2 text-sm"
                      >
                        <FileImage className="h-4 w-4" />
                        <span>OpenAI GPT-4 Vision</span>
                      </button>
                      <button
                        onClick={processBusinessCardWithGemini}
                        className="w-full bg-indigo-600 dark:bg-indigo-700 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 flex items-center justify-center space-x-2 text-sm"
                      >
                        <FileImage className="h-4 w-4" />
                        <span>Google Gemini Vision</span>
                      </button>
                      <button
                        onClick={processBusinessCardWithOpenRouter}
                        className="w-full bg-teal-600 dark:bg-teal-700 text-white px-4 py-2 rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 flex items-center justify-center space-x-2 text-sm"
                      >
                        <FileImage className="h-4 w-4" />
                        <span>Llama 3.2 Vision (OpenRouter)</span>
                      </button>
                      <button
                        onClick={processBusinessCardWithLLaVA}
                        className="w-full bg-orange-600 dark:bg-orange-700 text-white px-4 py-2 rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 flex items-center justify-center space-x-2 text-sm"
                      >
                        <FileImage className="h-4 w-4" />
                        <span>LLaVA (HuggingFace)</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Processing Indicator */}
              {(isProcessing || isProcessingOpenAI || isProcessingGemini || isProcessingOpenRouter || isProcessingLLaVA) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <RefreshCw className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Extracting Information...</p>
                      <p className="text-xs text-blue-700 dark:text-blue-400">
                        {isProcessingOpenAI ? 'Using OpenAI GPT-4 Vision' :
                         isProcessingGemini ? 'Using Google Gemini Vision' :
                         isProcessingOpenRouter ? 'Using Llama 3.2 Vision' :
                         isProcessingLLaVA ? 'Using LLaVA HuggingFace' :
                         'Using your tenant\'s configured AI model'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Extraction Method Badge */}
              {extractionMethod && !isProcessing && editedData && (
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Extraction Method:</span>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${
                      extractionMethod === 'openai' || extractionMethod === 'OpenAI' 
                        ? 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/50'
                        : extractionMethod === 'gemini' || extractionMethod === 'Gemini'
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/50'
                        : extractionMethod === 'openrouter' || extractionMethod === 'OpenRouter'
                        ? 'text-teal-600 dark:text-teal-400 bg-teal-100 dark:bg-teal-900/50'
                        : extractionMethod === 'llava' || extractionMethod === 'huggingface'
                        ? 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/50'
                        : 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/50'
                    }`}>
                      {extractionMethod === 'openai' ? 'OpenAI'
                        : extractionMethod === 'gemini' ? 'Gemini'
                        : extractionMethod === 'openrouter' ? 'Llama Vision'
                        : extractionMethod === 'llava' || extractionMethod === 'huggingface' ? 'LLaVA'
                        : extractionMethod === 'ocr' ? 'Tesseract OCR'
                        : extractionMethod === 'ocr-fallback' ? 'OCR (Fallback)'
                        : extractionMethod}
                    </span>
                  </div>
                </div>
              )}

              {/* Confidence Score */}
              {!isProcessing && editedData && confidence > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Confidence Score</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{Math.round(confidence * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        confidence > 0.8 ? 'bg-green-600 dark:bg-green-500' : 
                        confidence > 0.6 ? 'bg-yellow-600 dark:bg-yellow-500' : 'bg-red-600 dark:bg-red-500'
                      }`}
                      style={{ width: `${confidence * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Editable Information Form - Visible after extraction */}
          {editedData && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Contact Information
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {isProcessing ? 'Extracting information from image...' : 'Review and edit the extracted information below'}
                </p>
              </div>

              {/* Form Fields - Extracted Data */}
              <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {/* Personal Information */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    Personal Information
                  </h4>
                  
                  {/* Thai Name Section */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center">
                      🇹🇭 Thai Name (ชื่อภาษาไทย)
                    </h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">First Name (ชื่อ)</label>
                        <input
                          type="text"
                          value={editedData.person.firstName || ''}
                          onChange={(e) => updateEditedData('person.firstName', e.target.value)}
                          disabled={isProcessing}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                          placeholder="เช่น สมชาย"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Last Name (นามสกุล)</label>
                        <input
                          type="text"
                          value={editedData.person.lastName || ''}
                          onChange={(e) => updateEditedData('person.lastName', e.target.value)}
                          disabled={isProcessing}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                          placeholder="เช่น ใจดี"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Title (ตำแหน่ง)</label>
                      <input
                        type="text"
                        value={editedData.person.title || ''}
                        onChange={(e) => updateEditedData('person.title', e.target.value)}
                        disabled={isProcessing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        placeholder="เช่น ผู้จัดการ"
                      />
                    </div>
                  </div>

                  {/* English Name Section */}
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-3 flex items-center">
                      🇺🇸 English Name (ชื่อภาษาอังกฤษ)
                      <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-1 rounded">Optional</span>
                    </h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">First Name</label>
                        <input
                          type="text"
                          value={editedData.person.firstName_eng || ''}
                          onChange={(e) => updateEditedData('person.firstName_eng', e.target.value)}
                          disabled={isProcessing}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                          placeholder="e.g. Somchai"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Last Name</label>
                        <input
                          type="text"
                          value={editedData.person.lastName_eng || ''}
                          onChange={(e) => updateEditedData('person.lastName_eng', e.target.value)}
                          disabled={isProcessing}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                          placeholder="e.g. Jaidee"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Title</label>
                      <input
                        type="text"
                        value={editedData.person.title_eng || ''}
                        onChange={(e) => updateEditedData('person.title_eng', e.target.value)}
                        disabled={isProcessing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        placeholder="e.g. Manager"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    Contact Information
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Email</label>
                      <input
                        type="email"
                        value={editedData.person.email || ''}
                        onChange={(e) => updateEditedData('person.email', e.target.value)}
                        disabled={isProcessing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        placeholder="example@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={editedData.person.phone || ''}
                        onChange={(e) => updateEditedData('person.phone', e.target.value)}
                        disabled={isProcessing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        placeholder="08X-XXX-XXXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Company Information */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                    <Building className="h-4 w-4 mr-1" />
                    Company Information
                  </h4>
                  
                  {/* Thai Company Section */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center">
                      🇹🇭 Thai Company (บริษัทภาษาไทย)
                    </h5>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Company Name (ชื่อบริษัท)</label>
                      <input
                        type="text"
                        value={editedData.company.name || ''}
                        onChange={(e) => updateEditedData('company.name', e.target.value)}
                        disabled={isProcessing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        placeholder="เช่น บริษัท เทคโนโลยี จำกัด"
                      />
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Address (ที่อยู่)</label>
                      <textarea
                        value={editedData.company.address || ''}
                        onChange={(e) => updateEditedData('company.address', e.target.value)}
                        disabled={isProcessing}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        placeholder="ที่อยู่ภาษาไทย"
                      />
                    </div>
                  </div>

                  {/* English Company Section */}
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-3 flex items-center">
                      🇺🇸 English Company (บริษัทภาษาอังกฤษ)
                      <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-1 rounded">Optional</span>
                    </h5>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Company Name</label>
                      <input
                        type="text"
                        value={editedData.company.name_eng || ''}
                        onChange={(e) => updateEditedData('company.name_eng', e.target.value)}
                        disabled={isProcessing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        placeholder="e.g. Technology Company Limited"
                      />
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Address</label>
                      <textarea
                        value={editedData.company.address_eng || ''}
                        onChange={(e) => updateEditedData('company.address_eng', e.target.value)}
                        disabled={isProcessing}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        placeholder="English address"
                      />
                    </div>
                  </div>

                  {/* Website */}
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Website</label>
                    <input
                      type="url"
                      value={editedData.company.website || ''}
                      onChange={(e) => updateEditedData('company.website', e.target.value)}
                      disabled={isProcessing}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-600 sticky bottom-0 bg-white dark:bg-gray-800">
                  <button
                    onClick={createCustomer}
                    disabled={isCreatingCustomer || customerCreated || isProcessing}
                    className="w-full bg-green-600 dark:bg-green-700 text-white px-4 py-3 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50 flex items-center justify-center space-x-2 font-medium"
                  >
                    {isCreatingCustomer ? (
                      <>
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        <span>Creating Customer...</span>
                      </>
                    ) : customerCreated ? (
                      <>
                        <CheckCircle className="h-5 w-5" />
                        <span>Customer Created Successfully</span>
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-5 w-5" />
                        <span>Save to Customer</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-600 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {customerCreated && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <p className="text-green-600 dark:text-green-300">Customer created successfully from business card!</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCardScanner; 